import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiCheck, FiX, FiRotateCw, FiBookOpen } from 'react-icons/fi';

import { itemReviewsApi } from '@/services/itemReviews';
import { matrizSaebMatrixApi } from '@/services/matrizSaebMatrix';
import { PATHS } from '@/routes/paths';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import DescriptorDetail from './DescriptorDetail';

const DESCRIPTOR_CODE_RE = /^(D\d+|[259][A-Z]+\d+\.\d+)$/;

const STATUS_CONFIG = {
  em_revisao: { label: 'Em revisão', variant: 'warning' },
  aprovado: { label: 'Aprovado', variant: 'success' },
  reprovado: { label: 'Reprovado', variant: 'danger' },
  pede_revisao: { label: 'Pede revisão', variant: 'info' }
};

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

// Rubrica do MatrizSaeb::ContentJudge (M6). Ordem = utilidade pra decidir, não
// a ordem do hash do backend. Pesos espelham WEIGHTS: gabarito vale dobrado
// porque é o único defeito que corrompe a medida do aluno — e tem piso duro,
// gabarito <= 2 flaga o item mesmo com agregado alto.
const JUDGE_DIMENSIONS = [
  { key: 'correcao_gabarito', label: 'Correção do gabarito', weight: 2 },
  { key: 'alinhamento_descritor', label: 'Alinhamento ao descritor', weight: 1 },
  { key: 'plausibilidade_distratores', label: 'Plausibilidade dos distratores', weight: 1 },
  { key: 'qualidade_enunciado', label: 'Qualidade do enunciado', weight: 1 },
  { key: 'adequacao_nivel', label: 'Adequação ao nível', weight: 0.5 }
];

const JUDGE_THRESHOLD = 3.5;
const JUDGE_GABARITO_FLOOR = 2;

// Laudo do gate LLM-as-judge. O juiz NÃO aprova nada: ele só pontua e aponta
// motivos. A palavra final é do revisor — por isso este painel é informativo e
// não mexe em nenhum botão de decisão.
function JudgeReport({ score, judge }) {
  const hasReport = judge && typeof judge === 'object';
  const scores = hasReport ? judge.scores || {} : {};
  const reasons = Array.from((hasReport && judge.flag_reasons) || []).filter(Boolean);
  const issues = Array.from((hasReport && judge.issues) || []).filter(Boolean);

  if (score == null && !hasReport) {
    return (
      <div className="rounded-lg border border-neutral-lightest bg-white p-4 text-xs">
        <div className="mb-2 font-semibold text-foreground">Gate LLM-as-judge</div>
        <p className="leading-relaxed text-neutral">
          Sem laudo — item ainda não julgado, ou fora do escopo da rubrica (ENEM, item sem descritor ou sem gabarito).{' '}
          <strong className="text-foreground">Ausência de laudo não é aprovação:</strong> revise normalmente.
        </p>
      </div>
    );
  }

  const flagged = judge?.auto_flagged === true || (score != null && score < JUDGE_THRESHOLD);
  const gabarito = scores.correcao_gabarito;

  return (
    <div className={['rounded-lg border p-4 text-xs', flagged ? 'border-danger/40 bg-danger/5' : 'border-success/40 bg-success/5'].join(' ')}>
      <div className="mb-3 flex items-center justify-between">
        <span className="font-semibold text-foreground">Gate LLM-as-judge</span>
        <Badge variant={flagged ? 'danger' : 'success'}>{score != null ? `${Number(score).toFixed(2)} / 5` : '—'}</Badge>
      </div>

      <dl className="space-y-1 text-neutral">
        {JUDGE_DIMENSIONS.map(d => {
          const v = scores[d.key];
          const isGabaritoFloor = d.key === 'correcao_gabarito' && v != null && v <= JUDGE_GABARITO_FLOOR;
          return (
            <div
              key={d.key}
              className="flex items-baseline justify-between gap-2"
            >
              <dt className={isGabaritoFloor ? 'font-medium text-danger' : ''}>
                {d.label}
                <span className="ml-1 text-neutral-light">×{d.weight}</span>
              </dt>
              <dd className={['font-medium tabular-nums', isGabaritoFloor ? 'text-danger' : 'text-foreground'].join(' ')}>{v ?? '—'}</dd>
            </div>
          );
        })}
      </dl>

      {gabarito != null && gabarito <= JUDGE_GABARITO_FLOOR && (
        <p className="mt-3 font-medium text-danger">Gabarito ambíguo ou errado (≤ {JUDGE_GABARITO_FLOOR}). Item flagado mesmo com agregado alto — confira a resposta antes de qualquer coisa.</p>
      )}

      {reasons.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 font-semibold text-foreground">Motivos do flag</div>
          <ul className="list-disc space-y-1 pl-4 text-neutral">
            {reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {issues.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 font-semibold text-foreground">Apontamentos</div>
          <ul className="list-disc space-y-1 pl-4 text-neutral">
            {issues.map((it, i) => (
              <li key={i}>{typeof it === 'string' ? it : JSON.stringify(it)}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-3 text-[11px] leading-relaxed text-neutral-light">
        Parecer consultivo de IA{judge?.model ? ` (${judge.model}` : ''}
        {judge?.rubric_version ? `, rubrica ${judge.rubric_version})` : judge?.model ? ')' : ''}. O juiz é cego a imagens e não aprova nada — a decisão é sua.
      </p>
    </div>
  );
}

export default function ReviewItem() {
  const { itemReviewId } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [descriptorOpen, setDescriptorOpen] = useState(false);
  const [descriptorLoading, setDescriptorLoading] = useState(false);
  const [descriptorData, setDescriptorData] = useState(null);
  const [descriptorComponentLabel, setDescriptorComponentLabel] = useState('');

  const fetchReview = useCallback(async () => {
    setLoading(true);
    try {
      const json = await itemReviewsApi.show(itemReviewId);
      const node = json?.data;
      setReview(node ? { id: node.id, ...node.attributes } : null);
    } catch (e) {
      toast.error('Erro ao carregar item');
    } finally {
      setLoading(false);
    }
  }, [itemReviewId]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  const decide = useCallback(
    async action => {
      setSubmitting(true);
      try {
        const fn = {
          approve: itemReviewsApi.approve,
          reject: itemReviewsApi.reject,
          request_revision: itemReviewsApi.requestRevision
        }[action];
        await fn(itemReviewId, { notes });
        toast.success(
          {
            approve: 'Item aprovado',
            reject: 'Item reprovado',
            request_revision: 'Revisão solicitada'
          }[action]
        );
        navigate(PATHS.QUESTIONS.REVIEW_QUEUE);
      } catch (e) {
        toast.error(e?.response?.data?.errors?.[0]?.title || 'Falha na ação');
      } finally {
        setSubmitting(false);
      }
    },
    [itemReviewId, notes, navigate]
  );

  const openDescriptor = useCallback(async () => {
    const q = review?.question || {};
    const code = q.taxonomies?.map(t => t.name).find(n => DESCRIPTOR_CODE_RE.test(n));
    if (!code || !q.etapa) {
      toast.error('Não foi possível identificar o descritor desta questão.');
      return;
    }
    setDescriptorOpen(true);
    setDescriptorLoading(true);
    try {
      const json = await matrizSaebMatrixApi.show(q.etapa);
      let found = null;
      let componentLabel = '';
      (json?.components || []).forEach(c => {
        const hit = c.descritores.find(d => d.code === code);
        if (hit) {
          found = hit;
          componentLabel = c.label;
        }
      });
      if (!found) throw new Error('descritor não encontrado na matriz');
      setDescriptorData(found);
      setDescriptorComponentLabel(componentLabel);
    } catch (_e) {
      toast.error('Falha ao carregar o descritor na matriz.');
      setDescriptorOpen(false);
    } finally {
      setDescriptorLoading(false);
    }
  }, [review]);

  // Atalhos de teclado (A/R/E) — só fora de inputs/textarea
  const q = review?.question || {};
  const hasImage = [...(q.motivator_texts || []).map(t => t.body), q.description || ''].join(' ').includes('<img');
  const imageVerified = review?.image_verified;
  const blocksApproval = hasImage && imageVerified !== true;

  useEffect(() => {
    const onKey = e => {
      const tag = e.target.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      const key = e.key.toLowerCase();
      if (key === 'a' && !blocksApproval) decide('approve');
      if (key === 'r') decide('reject');
      if (key === 'e') decide('request_revision');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [decide, blocksApproval]);

  const breadcrumbs = {
    title: 'Banco de questões',
    nav: [
      {
        route: PATHS.QUESTIONS.REVIEW_QUEUE,
        name: 'Revisão pedagógica',
        isActive: false
      },
      {
        name: q.external_id || (review ? `Item #${review.id}` : '…'),
        isActive: true
      }
    ]
  };

  if (loading || !review) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <Loader />
      </DashboardContent>
    );
  }

  const alternatives = (q.alternatives || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const statusCfg = STATUS_CONFIG[review.status] ?? STATUS_CONFIG.em_revisao;

  const verifyImage = async verified => {
    setSubmitting(true);
    try {
      const json = await itemReviewsApi.verifyImage(itemReviewId, verified);
      const node = json?.data;
      setReview(node ? { id: node.id, ...node.attributes } : review);
      toast.success(verified ? 'Imagem marcada como coerente' : 'Imagem marcada como incoerente');
    } catch (e) {
      toast.error('Falha ao salvar verificação de imagem');
    } finally {
      setSubmitting(false);
    }
  };
  const descritor = q.taxonomies?.find(t => /^(D\d+|[259][A-Z]+\d+\.\d+)/.test(t.name))?.name;
  const etapaBadge = {
    serie_3_em: { label: '3ª EM', variant: 'info' },
    ano_9_ef: { label: '9º EF', variant: 'primary' },
    ano_5_ef: { label: '5º EF', variant: 'primary' },
    ano_2_ef: { label: '2º EF', variant: 'primary' }
  }[q.etapa];

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* Page header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="mb-0 text-xl font-bold text-foreground">{q.external_id || `Item #${review.id}`}</h2>
          <Badge
            variant={statusCfg.variant}
            size="default"
          >
            {statusCfg.label}
          </Badge>
          {etapaBadge && (
            <Badge
              variant={etapaBadge.variant}
              size="default"
            >
              {etapaBadge.label}
            </Badge>
          )}
          {descritor && (
            <Badge
              variant="neutral-lightest"
              size="default"
            >
              {descritor}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => decide('reject')}
            disabled={submitting}
          >
            <ButtonIcon>
              <FiX />
            </ButtonIcon>{' '}
            Reprovar <kbd className="ml-1 text-xs opacity-70">R</kbd>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => decide('request_revision')}
            disabled={submitting}
          >
            <ButtonIcon>
              <FiRotateCw />
            </ButtonIcon>{' '}
            Pedir revisão <kbd className="ml-1 text-xs opacity-70">E</kbd>
          </Button>
          <Button
            size="sm"
            onClick={() => decide('approve')}
            disabled={submitting || blocksApproval}
            title={blocksApproval ? 'Confirme que a imagem confere com o enunciado antes de aprovar' : ''}
          >
            <ButtonIcon>
              <FiCheck />
            </ButtonIcon>{' '}
            Aprovar <kbd className="ml-1 text-xs opacity-70">A</kbd>
          </Button>
        </div>
      </div>

      {/* Grid: item + decisão */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Painel esquerdo: estímulo + alternativas */}
        <section className="rounded-lg border border-neutral-lightest bg-white p-6 lg:col-span-2">
          {(q.motivator_texts || []).map(t => (
            <div
              key={t.id}
              className="prose prose-sm bg-neutral-lightest/40 mb-4 max-w-none rounded border-l-4 border-neutral-lightest p-4 [&_img]:my-2 [&_img]:max-w-full"
              dangerouslySetInnerHTML={{ __html: t.body }}
            />
          ))}

          <div
            className="prose prose-sm mb-4 max-w-none font-medium"
            dangerouslySetInnerHTML={{ __html: q.description }}
          />

          <ol className="space-y-2">
            {alternatives.map(a => (
              <li
                key={a.id}
                className={`rounded border p-3 ${a.is_correct ? 'bg-success/10 border-success' : 'border-neutral-lightest bg-white'}`}
              >
                <div className="flex gap-3">
                  <strong className="text-neutral">{a.name})</strong>
                  <span className="flex-1">{a.description}</span>
                  {a.is_correct && (
                    <Badge
                      variant="success"
                      size="xs"
                      className="h-fit"
                    >
                      Gabarito
                    </Badge>
                  )}
                </div>
                {(a.justification || a.distractor_error) && <p className="mt-2 text-xs italic text-neutral">{a.is_correct ? a.justification : a.distractor_error || a.justification}</p>}
              </li>
            ))}
          </ol>
        </section>

        {/* Painel direito: observações + metadados */}
        <aside className="space-y-4 lg:sticky lg:top-4">
          <div className="rounded-lg border border-neutral-lightest bg-white p-4">
            <Label
              htmlFor="review-notes"
              className="mb-2 block text-sm font-medium"
            >
              Observações da revisão
            </Label>
            <textarea
              id="review-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Anotações sobre a decisão (opcional)"
              rows={4}
              className="w-full rounded border border-neutral-lightest p-2 text-sm focus:border-primary focus:outline-none"
            />
            <p className="mt-2 text-xs text-neutral">
              Use os atalhos <kbd>A</kbd> aprovar · <kbd>E</kbd> pedir revisão · <kbd>R</kbd> reprovar.
            </p>
          </div>

          {hasImage && (
            <div className={`rounded-lg border p-4 text-xs ${imageVerified === true ? 'border-success/40 bg-success/5' : imageVerified === false ? 'border-danger/40 bg-danger/5' : 'border-warning/40 bg-warning/5'}`}>
              <div className="mb-2 font-semibold text-foreground">Verificação visual</div>
              <p className="mb-3 text-neutral">Esta questão tem imagem. Confirme se a imagem está coerente com o enunciado antes de aprovar.</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="xs"
                  variant={imageVerified === true ? 'default' : 'outline'}
                  onClick={() => verifyImage(true)}
                  disabled={submitting}
                >
                  <ButtonIcon>
                    <FiCheck />
                  </ButtonIcon>
                  {imageVerified === true ? 'Imagem confere' : 'Imagem confere'}
                </Button>
                <Button
                  size="xs"
                  variant={imageVerified === false ? 'destructive' : 'outline-danger'}
                  onClick={() => verifyImage(false)}
                  disabled={submitting}
                >
                  <ButtonIcon>
                    <FiX />
                  </ButtonIcon>
                  Imagem incorreta
                </Button>
              </div>
              {imageVerified === false && (
                <p className="mt-2 text-xs text-danger">
                  Marcada como incoerente. Considere <strong>Reprovar</strong> ou <strong>Pedir revisão</strong> para regerar.
                </p>
              )}
              {imageVerified == null && <p className="text-warning-dark mt-2 text-xs">Pendente — Aprovar fica bloqueado até decidir.</p>}
            </div>
          )}

          <div className="rounded-lg border border-neutral-lightest bg-white p-4 text-xs">
            <div className="mb-2 font-semibold text-foreground">Alinhamento à matriz</div>
            <dl className="space-y-1 text-neutral">
              <div className="flex justify-between">
                <dt>DOK target</dt>
                <dd className="font-medium text-foreground">{q.dok_target ?? '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt>Dificuldade alvo</dt>
                <dd className="font-medium text-foreground">{q.dificuldade_alvo ?? '—'}</dd>
              </div>
              {q.taxonomies?.length > 0 && (
                <div className="flex justify-between">
                  <dt>Taxonomias</dt>
                  <dd className="font-medium text-foreground">{q.taxonomies.map(t => t.name).join(' / ')}</dd>
                </div>
              )}
            </dl>
            {q.etapa && q.taxonomies?.some(t => DESCRIPTOR_CODE_RE.test(t.name)) && (
              <Button
                variant="outline"
                size="xs"
                className="mt-3 w-full"
                onClick={openDescriptor}
              >
                <ButtonIcon>
                  <FiBookOpen />
                </ButtonIcon>
                Ver descritor na matriz
              </Button>
            )}
          </div>

          <JudgeReport
            score={review.judge_score}
            judge={review.judge}
          />

          {review.haladyna_alerts && Object.keys(review.haladyna_alerts).length > 0 && (
            <details className="border-warning/40 bg-warning/10 rounded-lg border p-4 text-xs">
              <summary className="text-warning-dark cursor-pointer font-semibold">Alertas Haladyna ({Object.keys(review.haladyna_alerts).length})</summary>
              <pre className="mt-2 overflow-x-auto text-xs">{JSON.stringify(review.haladyna_alerts, null, 2)}</pre>
            </details>
          )}
        </aside>
      </div>

      <Dialog
        open={descriptorOpen}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setDescriptorOpen(false);
            setDescriptorData(null);
          }
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="font-mono">{descriptorData?.code ?? '...'}</DialogTitle>
            <DialogDescription>{descriptorData?.verbo}</DialogDescription>
          </DialogHeader>
          {descriptorLoading ? (
            <Loader />
          ) : (
            <DescriptorDetail
              d={descriptorData}
              componentLabel={descriptorComponentLabel}
            />
          )}
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
